import {
  DEFAULT_GRADER_MODEL,
  GRADER_MODEL_VARIABLE,
  GRADER_MODEL_VAR_REF,
} from '../../../services/grader.interface.js';

import type { BindingRow } from './bindings.utils.js';
import type { VariableRow } from './EvaluationTemplateForm.types.js';

export type BindingFlowConsumerKind = 'Agent' | 'Grader' | 'Grader as Agent';
export type BindingFlowSeverity = 'error' | 'warning' | 'info';

export interface BindingFlowVariableDeclaration {
  type?: string;
  default?: unknown;
}

export interface BindingFlowResource {
  kind: BindingFlowConsumerKind;
  name: string;
  version?: number;
  spec?: unknown;
  variables?: Record<string, BindingFlowVariableDeclaration>;
}

export interface BindingVariableMetadata {
  name: string;
  defaultValue?: unknown;
  declaredBy: string[];
  type?: string;
  templateVariableId?: string;
}

export interface BindingFlowIssue {
  id: string;
  severity: BindingFlowSeverity;
  message: string;
}

export interface BindingFlowEdge {
  id: string;
  source: string;
  binding: string;
  consumer: string;
  detail: string;
  severity?: BindingFlowSeverity;
}

export interface BindingFlowRequirement {
  id: string;
  component: string;
  parameter: string;
  source: string;
  preview: string;
  diagnostic?: string;
  severity?: BindingFlowSeverity;
}

export interface BindingFlowAnalysis {
  edges: BindingFlowEdge[];
  requirements: BindingFlowRequirement[];
  issues: BindingFlowIssue[];
  estimatedTrials: number | null;
  queryItemCount: number | null;
  dataItemCount: number | null;
  hasErrors: boolean;
}

export interface BindingFlowDiagnostics {
  querySet: string[];
  dataSet: string[];
  inputs: Record<string, string[]>;
  components: string[];
}

export interface GraderModelFallback {
  resources: BindingFlowResource[];
  shouldDeclare: boolean;
}

export function applyGraderModelFallback(
  resources: BindingFlowResource[],
  templateVariables: VariableRow[],
): GraderModelFallback {
  const referencingGraders = resources.filter(
    (resource) =>
      resource.kind === 'Grader' &&
      JSON.stringify(resource.spec)?.includes(GRADER_MODEL_VAR_REF),
  );
  const hasDeclaration =
    templateVariables.some(
      (variable) => variable.name.trim() === GRADER_MODEL_VARIABLE,
    ) ||
    resources.some(
      (resource) => resource.variables?.[GRADER_MODEL_VARIABLE] !== undefined,
    );
  if (referencingGraders.length === 0 || hasDeclaration) {
    return { resources, shouldDeclare: false };
  }

  const referencingNames = new Set(
    referencingGraders.map((resource) => resource.name),
  );
  return {
    resources: resources.map((resource) =>
      resource.kind === 'Grader' && referencingNames.has(resource.name)
        ? {
            ...resource,
            variables: {
              ...resource.variables,
              [GRADER_MODEL_VARIABLE]: {
                type: 'String',
                default: DEFAULT_GRADER_MODEL,
              },
            },
          }
        : resource,
    ),
    shouldDeclare: true,
  };
}

export interface AnalyzeBindingFlowParams {
  resources: BindingFlowResource[];
  templateVariables: VariableRow[];
  bindings: BindingRow[];
  queryItems: string[] | null;
  dataItems: string[] | null;
  dataSetEnabled: boolean;
  dataSetInspectable: boolean;
}

export function addBindingFlowError(
  analysis: BindingFlowAnalysis,
  id: string,
  message: string,
): BindingFlowAnalysis {
  return {
    ...analysis,
    hasErrors: true,
    issues: [...analysis.issues, { id, severity: 'error', message }],
  };
}

export function classifyBindingFlowDiagnostics(
  analysis: BindingFlowAnalysis,
): BindingFlowDiagnostics {
  const querySet = new Set<string>();
  const dataSet = new Set<string>();
  const components = new Set<string>();
  const inputs = new Map<string, Set<string>>();
  const classified = new Set<string>();

  for (const requirement of analysis.requirements) {
    if (requirement.diagnostic === undefined) continue;
    const message = requirement.diagnostic;
    classified.add(message);
    if (
      requirement.source.startsWith('QuerySet') ||
      requirement.parameter.includes('$.queryItem') ||
      requirement.parameter.includes('$.qi')
    ) {
      querySet.add(message);
      continue;
    }
    if (
      requirement.source.startsWith('DataSet') ||
      requirement.parameter.includes('$.dataItem') ||
      requirement.parameter.includes('$.di')
    ) {
      dataSet.add(message);
      continue;
    }
    const variable = /^\$var:([A-Z][A-Z0-9_]*)$/.exec(
      requirement.parameter,
    )?.[1];
    if (variable !== undefined) {
      const messages = inputs.get(variable) ?? new Set<string>();
      messages.add(message);
      inputs.set(variable, messages);
      continue;
    }
    components.add(message);
  }

  for (const issue of analysis.issues) {
    if (classified.has(issue.message)) continue;
    if (issue.message.startsWith('QuerySet.')) {
      querySet.add(issue.message);
      continue;
    }
    if (
      issue.message.startsWith('DataSet.') ||
      issue.message.includes('the DataSet is disabled')
    ) {
      dataSet.add(issue.message);
      continue;
    }
    const variable = /\bVariable ([A-Z][A-Z0-9_]*)\b/.exec(issue.message)?.[1];
    if (variable !== undefined) {
      const messages = inputs.get(variable) ?? new Set<string>();
      messages.add(issue.message);
      inputs.set(variable, messages);
    } else {
      components.add(issue.message);
    }
  }

  return {
    querySet: [...querySet],
    dataSet: [...dataSet],
    inputs: Object.fromEntries(
      [...inputs.entries()].map(([name, messages]) => [name, [...messages]]),
    ),
    components: [...components],
  };
}

interface TemplateReference {
  kind: 'expr' | 'var';
  value: string;
  resource: BindingFlowResource;
  propertyPath: string;
}

interface ResolvedSource {
  source: string;
  detail: string;
  severity?: BindingFlowSeverity;
  issue?: string;
}

const TEMPLATE_REFERENCE_PATTERN = /\{\{\$(expr|var):(.+?)\}\}/g;
const SIMPLE_ITEM_PATH_PATTERN =
  /^\$\.(queryItem|qi|dataItem|di)\.data((?:\.[A-Za-z_$][A-Za-z0-9_$]*)*)$/;
const TURN_PATH_PATTERN = /^\$\.turn\.output(?:\.|\[|$)/;

function hasDeclaredDefault(value: unknown): boolean {
  return value !== undefined && value !== null;
}

export function analyzeBindingFlow(
  params: AnalyzeBindingFlowParams,
): BindingFlowAnalysis {
  const references = collectTemplateReferences(params.resources);
  const declarations = collectDeclarations(
    params.templateVariables,
    params.resources,
  );
  const bindingByName = new Map(
    params.bindings.map((binding) => [binding.name, binding.expr.trim()]),
  );
  const referencedVariableNames = new Set(
    references
      .filter((reference) => reference.kind === 'var')
      .map((reference) => reference.value),
  );
  const edges: BindingFlowEdge[] = [];
  const issues: BindingFlowIssue[] = declarationConflictIssues(declarations);
  const requirements = buildRequirements(
    references,
    declarations,
    bindingByName,
    params,
  );

  for (const reference of references) {
    if (reference.kind === 'expr') {
      addExpressionEdge(edges, issues, reference, reference.value, params);
      continue;
    }

    const declaration = declarations.get(reference.value);
    if (declaration === undefined) {
      const message = `Variable ${reference.value} is referenced by ${consumerLabel(reference)} but is not declared by the template or selected resources.`;
      edges.push({
        id: referenceId(reference),
        source: 'Missing declaration',
        binding: `$var:${reference.value}`,
        consumer: consumerLabel(reference),
        detail: message,
        severity: 'error',
      });
      issues.push({
        id: `${referenceId(reference)}-missing`,
        severity: 'error',
        message,
      });
      continue;
    }

    const binding = bindingByName.get(reference.value) ?? '';
    if (binding.length > 0) {
      const expressions = extractReferences(binding).filter(
        (item) => item.kind === 'expr',
      );
      if (expressions.length === 0) {
        edges.push({
          id: referenceId(reference),
          source: 'Literal binding',
          binding: `$var:${reference.value}`,
          consumer: consumerLabel(reference),
          detail: binding,
        });
      } else {
        for (const expression of expressions) {
          addExpressionEdge(
            edges,
            issues,
            reference,
            expression.value,
            params,
            `$var:${reference.value}`,
          );
        }
      }
      continue;
    }

    if (declaration.hasDefault) {
      edges.push({
        id: referenceId(reference),
        source: `Default (${formatValue(declaration.defaultValue)})`,
        binding: `$var:${reference.value}`,
        consumer: consumerLabel(reference),
        detail: `Uses the declared default because no per-trial binding is set.`,
      });
      continue;
    }

    const message = `Variable ${reference.value} has no default or per-trial binding. A run-time value must be supplied before this consumer executes.`;
    edges.push({
      id: referenceId(reference),
      source: 'Run-time value required',
      binding: `$var:${reference.value}`,
      consumer: consumerLabel(reference),
      detail: message,
      severity: 'warning',
    });
    issues.push({
      id: `${referenceId(reference)}-unbound`,
      severity: 'warning',
      message,
    });
  }

  for (const bindingRow of params.bindings) {
    if (referencedVariableNames.has(bindingRow.name)) continue;
    const declaration = declarations.get(bindingRow.name);
    const expression = bindingRow.expr.trim();
    edges.push({
      id: `unused-variable-${bindingRow.name}`,
      source:
        expression.length > 0
          ? 'Configured binding'
          : declaration?.hasDefault === true
            ? `Default (${formatValue(declaration.defaultValue)})`
            : 'Run-time value',
      binding: `$var:${bindingRow.name}`,
      consumer: 'No selected $var consumer',
      detail:
        expression.length > 0
          ? expression
          : 'The variable is declared but is not referenced by the selected Agent or Graders.',
      severity: 'info',
    });
  }

  const queryItemCount = params.queryItems?.length ?? null;
  const dataItemCount = params.dataSetEnabled
    ? (params.dataItems?.length ?? null)
    : 1;
  const estimatedTrials =
    queryItemCount !== null && dataItemCount !== null
      ? queryItemCount * dataItemCount
      : null;

  const uniqueIssues = deduplicateBy(issues, (issue) => issue.message);
  return {
    edges: deduplicateBy(
      edges,
      (edge) =>
        `${edge.source}|${edge.binding}|${edge.consumer}|${edge.detail}`,
    ),
    requirements,
    issues: uniqueIssues,
    estimatedTrials,
    queryItemCount,
    dataItemCount,
    hasErrors: uniqueIssues.some((issue) => issue.severity === 'error'),
  };
}

function buildRequirements(
  references: TemplateReference[],
  declarations: Map<string, CollectedDeclaration>,
  bindingByName: Map<string, string>,
  params: AnalyzeBindingFlowParams,
): BindingFlowRequirement[] {
  const requirements = references.map((reference): BindingFlowRequirement => {
    if (reference.kind === 'expr') {
      const resolved = resolveExpressionSource(
        reference.value,
        reference,
        params,
      );
      return {
        id: `${referenceId(reference)}-requirement`,
        component: resourceLabel(reference.resource),
        parameter: `$expr:${reference.value}`,
        source: expressionRequirementSource(reference.value),
        preview: expressionPreview(reference.value, params),
        diagnostic: resolved.issue,
        severity: resolved.severity,
      };
    }
    const declaration = declarations.get(reference.value);
    const binding = bindingByName.get(reference.value) ?? '';
    if (declaration === undefined) {
      return {
        id: `${referenceId(reference)}-requirement`,
        component: resourceLabel(reference.resource),
        parameter: `$var:${reference.value}`,
        source: 'Missing declaration',
        preview: 'Unavailable',
        diagnostic: `Variable ${reference.value} is referenced by ${consumerLabel(reference)} but is not declared by the template or selected resources.`,
        severity: 'error',
      };
    }
    if (binding.length > 0) {
      const boundExpression = extractReferences(binding).find(
        (item) => item.kind === 'expr',
      );
      const resolved =
        boundExpression === undefined
          ? undefined
          : resolveExpressionSource(boundExpression.value, reference, params);
      return {
        id: `${referenceId(reference)}-requirement`,
        component: resourceLabel(reference.resource),
        parameter: `$var:${reference.value}`,
        source:
          boundExpression === undefined
            ? 'Literal binding'
            : `${expressionRequirementSource(boundExpression.value)} via binding`,
        preview: bindingPreview(binding, params),
        diagnostic: resolved?.issue,
        severity: resolved?.severity,
      };
    }
    if (declaration.hasDefault) {
      return {
        id: `${referenceId(reference)}-requirement`,
        component: resourceLabel(reference.resource),
        parameter: `$var:${reference.value}`,
        source: `Default (${formatValue(declaration.defaultValue)})`,
        preview: formatPreviewValue(declaration.defaultValue),
      };
    }
    return {
      id: `${referenceId(reference)}-requirement`,
      component: resourceLabel(reference.resource),
      parameter: `$var:${reference.value}`,
      source: 'Run-time value required',
      preview: 'Supplied when the run is queued',
      diagnostic: `Variable ${reference.value} has no default or per-trial binding. A run-time value must be supplied before this consumer executes.`,
      severity: 'warning',
    };
  });
  return deduplicateBy(
    requirements,
    (requirement) =>
      `${requirement.component}|${requirement.parameter}|${requirement.source}`,
  );
}

function bindingPreview(
  binding: string,
  params: AnalyzeBindingFlowParams,
): string {
  const rendered = binding.replace(
    /\{\{\$(expr|var):(.+?)\}\}/g,
    (token: string, kind: string, value: string) =>
      kind === 'expr' ? expressionPreview(value, params) : token,
  );
  return formatPreviewValue(rendered);
}

function expressionPreview(
  expression: string,
  params: AnalyzeBindingFlowParams,
): string {
  const trimmed = expression.trim();
  if (TURN_PATH_PATTERN.test(trimmed)) {
    return 'Available after the Agent runs';
  }
  const match = SIMPLE_ITEM_PATH_PATTERN.exec(trimmed);
  if (match === null) return 'Resolved during the run';

  const root = match[1]!;
  const isQuery = root === 'queryItem' || root === 'qi';
  if (!isQuery && !params.dataSetEnabled) return 'DataSet is disabled';
  if (!isQuery && !params.dataSetInspectable) {
    return 'External DataSet preview unavailable';
  }
  const items = isQuery ? params.queryItems : params.dataItems;
  if (items === null || items.length === 0) return 'No row available';

  const path = match[2]!
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const resolved = itemValueAtPath(items[0]!, path);
  return resolved.found
    ? formatPreviewValue(resolved.value)
    : 'Missing in the first row';
}

function expressionRequirementSource(expression: string): string {
  const trimmed = expression.trim();
  if (/^\$\.(queryItem|qi)(?:\.|$)/.test(trimmed)) return 'QuerySet';
  if (/^\$\.(dataItem|di)(?:\.|$)/.test(trimmed)) return 'DataSet';
  if (/^\$\.turn(?:\.|\[|$)/.test(trimmed)) return 'Agent output';
  return 'Run-time expression';
}

export function collectBindingVariableNames(
  templateVariables: VariableRow[],
  resources: BindingFlowResource[],
): string[] {
  const names = new Set(
    templateVariables
      .map((variable) => variable.name.trim())
      .filter((name) => name.length > 0),
  );
  for (const resource of resources) {
    for (const name of Object.keys(resource.variables ?? {})) {
      names.add(name);
    }
  }
  return [...names].sort((left, right) => left.localeCompare(right));
}

export function collectBindingVariables(
  templateVariables: VariableRow[],
  resources: BindingFlowResource[],
): BindingVariableMetadata[] {
  const unnamedTemplateVariables: BindingVariableMetadata[] = [];
  const collected = new Map<
    string,
    {
      hasDefault: boolean;
      defaultValue?: unknown;
      declaredBy: Set<string>;
      type?: string;
      templateVariableId?: string;
    }
  >();

  for (const resource of resources) {
    for (const [name, declaration] of Object.entries(
      resource.variables ?? {},
    )) {
      const current = collected.get(name) ?? {
        hasDefault: false,
        declaredBy: new Set<string>(),
      };
      current.declaredBy.add(`${resource.kind} ${resource.name}`);
      if (!current.hasDefault && hasDeclaredDefault(declaration.default)) {
        current.hasDefault = true;
        current.defaultValue = declaration.default;
      }
      current.type ??= declaration.type;
      collected.set(name, current);
    }
  }

  for (const variable of templateVariables) {
    const name = variable.name.trim();
    if (name.length === 0) {
      unnamedTemplateVariables.push({
        name: '',
        declaredBy: ['Template'],
        type: variable.type,
        templateVariableId: variable.id,
      });
      continue;
    }
    const hasDefault = variable.defaultValue.trim().length > 0;
    const current = collected.get(name) ?? {
      hasDefault: false,
      declaredBy: new Set<string>(),
    };
    current.declaredBy.add('Template');
    current.hasDefault = hasDefault;
    current.defaultValue = hasDefault ? variable.defaultValue : undefined;
    current.type = variable.type;
    current.templateVariableId = variable.id;
    collected.set(name, current);
  }

  return [...collected.entries()]
    .map(([name, metadata]) => ({
      name,
      ...(metadata.hasDefault ? { defaultValue: metadata.defaultValue } : {}),
      declaredBy: [...metadata.declaredBy],
      ...(metadata.type !== undefined ? { type: metadata.type } : {}),
      ...(metadata.templateVariableId !== undefined
        ? { templateVariableId: metadata.templateVariableId }
        : {}),
    }))
    .sort((left, right) => left.name.localeCompare(right.name))
    .concat(unnamedTemplateVariables);
}

export function variableHasDefault(
  name: string,
  templateVariables: VariableRow[],
  resources: BindingFlowResource[],
): boolean {
  const templateVariable = templateVariables.find(
    (variable) => variable.name.trim() === name,
  );
  if (templateVariable !== undefined) {
    return templateVariable.defaultValue.trim().length > 0;
  }
  return resources.some((resource) => {
    const declaration = resource.variables?.[name];
    return hasDeclaredDefault(declaration?.default);
  });
}

function addExpressionEdge(
  edges: BindingFlowEdge[],
  issues: BindingFlowIssue[],
  reference: TemplateReference,
  expression: string,
  params: AnalyzeBindingFlowParams,
  binding = `$expr:${expression}`,
): void {
  const resolved = resolveExpressionSource(expression, reference, params);
  const id = `${referenceId(reference)}-${String(edges.length)}`;
  edges.push({
    id,
    source: resolved.source,
    binding,
    consumer: consumerLabel(reference),
    detail: resolved.detail,
    severity: resolved.severity,
  });
  if (resolved.issue !== undefined) {
    issues.push({
      id: `${id}-issue`,
      severity: resolved.severity ?? 'info',
      message: resolved.issue,
    });
  }
}

function resolveExpressionSource(
  expression: string,
  reference: TemplateReference,
  params: AnalyzeBindingFlowParams,
): ResolvedSource {
  const trimmed = expression.trim();
  if (TURN_PATH_PATTERN.test(trimmed)) {
    if (reference.resource.kind !== 'Grader') {
      const issue = `${consumerLabel(reference)} reads $.turn.output before the Agent has produced a turn.`;
      return {
        source: 'Agent output (unavailable)',
        detail: trimmed,
        severity: 'error',
        issue,
      };
    }
    return {
      source: 'Agent output',
      detail: trimmed,
    };
  }

  const match = SIMPLE_ITEM_PATH_PATTERN.exec(trimmed);
  if (match === null) {
    return {
      source: 'Run-time expression',
      detail: trimmed,
      severity: 'info',
    };
  }

  const root = match[1]!;
  const path = match[2]!
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const isQuery = root === 'queryItem' || root === 'qi';
  const sourceName = isQuery ? 'QuerySet' : 'DataSet';
  const displayPath = path.length > 0 ? path.join('.') : 'data';
  const items = isQuery ? params.queryItems : params.dataItems;

  if (!isQuery && !params.dataSetEnabled) {
    const issue = `${consumerLabel(reference)} reads DataSet.${displayPath}, but the DataSet is disabled.`;
    return {
      source: `DataSet.${displayPath} (missing)`,
      detail: trimmed,
      severity: 'error',
      issue,
    };
  }

  if (!isQuery && !params.dataSetInspectable) {
    return {
      source: `DataSet.${displayPath}`,
      detail: `The selected external DataSet cannot be inspected before the run.`,
      severity: 'info',
    };
  }

  if (items === null) {
    const issue = `${sourceName}.${displayPath} cannot be validated because no parsed ${sourceName} rows are available.`;
    return {
      source: `${sourceName}.${displayPath} (unverified)`,
      detail: trimmed,
      severity: 'warning',
      issue,
    };
  }

  const missingCount = items.filter((item) => !itemHasPath(item, path)).length;
  if (missingCount > 0) {
    const issue = `${sourceName}.${displayPath} is missing from ${String(missingCount)} of ${String(items.length)} rows used by ${consumerLabel(reference)}.`;
    return {
      source: `${sourceName}.${displayPath} (missing rows)`,
      detail: trimmed,
      severity: 'error',
      issue,
    };
  }

  return {
    source: `${sourceName}.${displayPath}`,
    detail: `${String(items.length)} of ${String(items.length)} rows contain this path.`,
  };
}

function collectTemplateReferences(
  resources: BindingFlowResource[],
): TemplateReference[] {
  const references: TemplateReference[] = [];
  for (const resource of resources) {
    walkStrings(resource.spec, '', (text, propertyPath) => {
      for (const reference of extractReferences(text)) {
        references.push({ ...reference, resource, propertyPath });
      }
    });
  }
  return references;
}

function extractReferences(
  text: string,
): { kind: 'expr' | 'var'; value: string }[] {
  const references: { kind: 'expr' | 'var'; value: string }[] = [];
  for (const match of text.matchAll(TEMPLATE_REFERENCE_PATTERN)) {
    const kind = match[1];
    const value = match[2]?.trim();
    if ((kind === 'expr' || kind === 'var') && value !== undefined) {
      references.push({ kind, value });
    }
  }
  return references;
}

function walkStrings(
  value: unknown,
  path: string,
  visit: (text: string, path: string) => void,
): void {
  if (typeof value === 'string') {
    visit(value, path);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      walkStrings(item, `${path}[${String(index)}]`, visit),
    );
    return;
  }
  if (value === null || typeof value !== 'object') return;
  for (const [key, child] of Object.entries(value)) {
    walkStrings(child, path.length > 0 ? `${path}.${key}` : key, visit);
  }
}

function itemHasPath(item: string, path: string[]): boolean {
  return itemValueAtPath(item, path).found;
}

function itemValueAtPath(
  item: string,
  path: string[],
): { found: boolean; value?: unknown } {
  let current: unknown;
  try {
    current = JSON.parse(item) as unknown;
  } catch {
    current = item;
  }
  for (const segment of path) {
    if (
      current === null ||
      typeof current !== 'object' ||
      Array.isArray(current) ||
      !Object.prototype.hasOwnProperty.call(current, segment)
    ) {
      return { found: false };
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return { found: true, value: current };
}

interface CollectedDeclaration {
  hasDefault: boolean;
  defaultValue?: unknown;
  signatures: Set<string>;
}

function collectDeclarations(
  templateVariables: VariableRow[],
  resources: BindingFlowResource[],
): Map<string, CollectedDeclaration> {
  const declarations = new Map<string, CollectedDeclaration>();
  for (const resource of resources) {
    for (const [name, declaration] of Object.entries(
      resource.variables ?? {},
    )) {
      addDeclaration(
        declarations,
        name,
        declaration.type,
        declaration.default,
        hasDeclaredDefault(declaration.default),
      );
    }
  }
  for (const variable of templateVariables) {
    const name = variable.name.trim();
    if (name.length === 0) continue;
    const hasDefault = variable.defaultValue.trim().length > 0;
    declarations.set(name, {
      hasDefault,
      defaultValue: hasDefault ? variable.defaultValue : undefined,
      signatures: new Set([
        declarationSignature(
          variable.type,
          hasDefault ? variable.defaultValue : undefined,
          hasDefault,
        ),
      ]),
    });
  }
  return declarations;
}

function addDeclaration(
  declarations: Map<string, CollectedDeclaration>,
  name: string,
  type: string | undefined,
  defaultValue: unknown,
  hasDefault: boolean,
): void {
  const signature = declarationSignature(type, defaultValue, hasDefault);
  const existing = declarations.get(name);
  if (existing === undefined) {
    declarations.set(name, {
      hasDefault,
      defaultValue,
      signatures: new Set([signature]),
    });
    return;
  }
  existing.signatures.add(signature);
}

function declarationConflictIssues(
  declarations: Map<string, CollectedDeclaration>,
): BindingFlowIssue[] {
  return [...declarations.entries()]
    .filter(([, declaration]) => declaration.signatures.size > 1)
    .map(([name]) => ({
      id: `conflict-${name}`,
      severity: 'error' as const,
      message: `Variable ${name} has conflicting type or default declarations across the selected resources. Declare it on the template to provide one authoritative definition.`,
    }));
}

function declarationSignature(
  type: string | undefined,
  defaultValue: unknown,
  hasDefault: boolean,
): string {
  return `${type?.toLowerCase() ?? ''}|${hasDefault ? formatValue(defaultValue) : '<none>'}`;
}

function consumerLabel(reference: TemplateReference): string {
  return `${resourceLabel(reference.resource)} · ${reference.propertyPath}`;
}

function resourceLabel(resource: BindingFlowResource): string {
  const version =
    resource.version === undefined ? '' : ` v${String(resource.version)}`;
  return `${resource.kind} ${resource.name}${version}`;
}

function referenceId(reference: TemplateReference): string {
  return `${reference.resource.kind}-${reference.resource.name}-${reference.propertyPath}-${reference.kind}-${reference.value}`;
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function formatPreviewValue(value: unknown): string {
  const formatted = formatValue(value);
  const singleLine = formatted.replace(/\s+/g, ' ').trim();
  return singleLine.length <= 160
    ? singleLine
    : `${singleLine.slice(0, 157)}...`;
}

function deduplicateBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const itemKey = key(item);
    if (seen.has(itemKey)) return false;
    seen.add(itemKey);
    return true;
  });
}
